import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { LineChart as Chart, RefreshCw, ExternalLink } from 'lucide-react';
import { MarketSwapForm } from './MarketSwapForm';
import { MarketAddFundsForm } from './MarketAddFundsForm';
import { DeployLogConsole } from './DeployLogConsole';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v, d = 2) => Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: d });

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg bg-slate-900/60 p-3"><p className="text-xs text-slate-400">{label}</p><p className="text-white font-semibold" data-testid={testId}>{value}</p></div>
);

export const BethMarketPanel = ({ token }) => {
  const [state, setState] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try { setState((await axios.get(`${API}/pas-admin/market/state`, { headers })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load market'); }
  };

  const poll = async (jobId) => {
    clearTimeout(pollRef.current);
    try {
      const r = await axios.get(`${API}/pas-admin/market/jobs/${jobId}`, { headers });
      setJob(r.data);
      if (r.data.status === 'running') { pollRef.current = setTimeout(() => poll(jobId), 3000); return; }
      if (r.data.status === 'completed') toast.success('Done'); else toast.error(r.data.error || 'Failed');
      load();
    } catch (e) { pollRef.current = setTimeout(() => poll(jobId), 5000); }
  };

  useEffect(() => { load(); return () => clearTimeout(pollRef.current); }, [token]);

  const pool = state?.pool;
  const busy = job?.status === 'running';
  return (
    <div className="space-y-6">
      <div className="bg-slate-800 border border-slate-700 rounded-xl" data-testid="beth-market-panel">
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3"><Chart className="w-5 h-5 text-purple-400" /><h3 className="text-white font-semibold">BETH Market · Uniswap (Mainnet)</h3></div>
          <button onClick={load} data-testid="market-refresh-btn" className="p-2 text-slate-400 hover:text-white rounded"><RefreshCw className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-5">
          {state?.wallet?.key_problem && <p className="text-sm text-red-300" data-testid="market-key-problem">{state.wallet.key_problem}</p>}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Pool" testId="market-pool-status" value={!state ? '…' : pool?.exists ? (pool.liquidity > 0 ? 'Active' : 'Empty') : 'Not created'} />
            <Stat label="Pool price" testId="market-pool-price" value={pool?.exists ? `$${fmt(pool.price_usdc_per_beth, 6)}` : `$${fmt(state?.reference_usdc_per_beth, 6)} (start price)`} />
            <Stat label="In pool" testId="market-pool-balances" value={pool?.exists ? `${fmt(pool.usdc_in_pool)} USDC · ${fmt(pool.beth_in_pool, 0)} BETH` : '—'} />
            <Stat label="Funding wallet" testId="market-wallet-balances" value={state ? `${fmt(state.wallet.beth, 0)} BETH · ${fmt(state.wallet.usdc)} USDC · ${fmt(state.wallet.eth, 4)} ETH` : '…'} />
          </div>
          {pool?.address && (
            <a href={`https://etherscan.io/address/${pool.address}`} target="_blank" rel="noreferrer" className="text-xs text-blue-400 flex items-center gap-1" data-testid="market-pool-link">
              <ExternalLink className="w-3 h-3" /> Pool on Etherscan
            </a>
          )}
          <div className="grid lg:grid-cols-2 gap-4">
            <MarketAddFundsForm token={token} state={state} busy={busy} onStarted={poll} onReload={load} />
            <MarketSwapForm token={token} state={state} busy={busy} onStarted={poll} />
          </div>
        </div>
      </div>
      <DeployLogConsole job={job} onClose={() => setJob(null)} />
    </div>
  );
};

export default BethMarketPanel;
